'use client'
import { AuthMessage, Messages, type AuthMessageCopy, type AuthMessageId } from '@/components/auth/auth-message'
import Background from '@/components/auth/background'
import JoinForm, { defaultJoinFormCopy, type JoinFormCopy } from '@/components/auth/join'
import LoginForm, { defaultLoginFormCopy, type LoginFormCopy } from '@/components/auth/login'
import { FlashMessageContext } from '@/context/flash-message'
import { AuthView } from '@/context/auth-modal-context'
import { appendParams } from '@/util/url'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import SegmentedControl from 'ui/src/components/controls/segmented-control'
import { CloseIcon } from 'ui/src/icons/navigation'

// Tailwind rather than styled-components: styled-components generates different
// componentId hashes in the server and client bundles here, which React reports
// as a hydration mismatch on every auth render. Same reasoning as the
// privacy-terms and search-bar conversions.
const outerContainerClassName = 'flex h-screen md:w-[50vw]'
const containerClassName = 'relative m-auto flex w-[325px] flex-col items-stretch gap-3'
const modalCloseButtonClassName =
    'inline-flex size-6 cursor-pointer items-center justify-center self-start border-0 bg-transparent p-0'
const closeIconClassName = 'absolute inset-0 mt-2.5 block self-start md:hidden'
const segmentedControlClassName = 'flex w-[325px] self-center'
const logoClassName = 'mx-auto h-[92px] w-[195px] md:h-[150px] md:w-[320px]'

type Props = {
    copy?: AuthPageCopy
    /** Rendered below the auth options. Server-rendered content is fine here. */
    footer?: ReactNode
    initialView?: AuthView
    messageId?: AuthMessageId
    mode?: 'modal' | 'page'
    next?: string
    onAuthSuccess?: (() => void | Promise<void>) | undefined
    onClose?: (() => void) | undefined
    onViewChange?: ((view: AuthView) => void) | undefined
}

export type AuthPageCopy = {
    join: JoinFormCopy
    login: LoginFormCopy
    logoAlt: string
    messages: Record<AuthMessageId, AuthMessageCopy>
    tabs: {
        login: string
        signUp: string
    }
}

export const defaultAuthPageCopy: AuthPageCopy = {
    join: defaultJoinFormCopy,
    login: defaultLoginFormCopy,
    logoAlt: 'Be Animated',
    messages: Messages,
    tabs: {
        login: 'Log In',
        signUp: 'Sign Up',
    },
}

const LoginPageContents = ({
    copy = defaultAuthPageCopy,
    footer,
    initialView,
    messageId,
    mode = 'page',
    next,
    onAuthSuccess,
    onClose,
    onViewChange,
}: Props) => {
    const message = messageId && copy.messages[messageId]
    const params = useSearchParams()
    const router = useRouter()
    const isModal = mode === 'modal'
    const routeView = usePathname() === '/login' ? 'login' : 'join'
    const [modalView, setModalView] = useState<AuthView>(initialView || routeView)
    const { showMessage } = useContext(FlashMessageContext)

    useEffect(() => {
        if (isModal) {
            setModalView(initialView || 'login')
        }
    }, [initialView, isModal])

    useEffect(() => {
        if (message) {
            showMessage({ message: <AuthMessage {...message} />, type: message.type })
        }
    }, [message, showMessage])

    const activeView = isModal ? modalView : routeView
    const isLogin = activeView === 'login'
    const resolvedNext = useMemo(() => {
        if (typeof next !== 'undefined') {
            return next
        }
        return params?.get('next') || undefined
    }, [next, params])

    const setView = (view: AuthView) => {
        if (isModal) {
            setModalView(view)
            onViewChange?.(view)
            return
        }
        const p = params ? Object.fromEntries(params.entries()) : {}
        const url = appendParams(new URL('/' + (view === 'login' ? 'login' : 'join'), location.href), p)
        router.push(url.toString())
    }

    const content = (
        <div className={containerClassName}>
            {isModal ? (
                <button
                    className={modalCloseButtonClassName}
                    onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        onClose?.()
                    }}
                    type="button"
                >
                    <CloseIcon height={20} />
                </button>
            ) : (
                <Link href="/">
                    <CloseIcon className={closeIconClassName} height={20} />
                </Link>
            )}
            {}
            <img
                className={logoClassName}
                src={`/static/img/be-animated.gif`}
                alt={copy.logoAlt}
                suppressHydrationWarning
            />
            <SegmentedControl
                className={segmentedControlClassName}
                key={`segmented-control-${isLogin ? 'login' : 'join'}-${mode}`}
                defaultIndex={isLogin ? 0 : 1}
                size="medium"
                gradient={isLogin ? 'blue-green' : 'purple-indigo'}
                options={[copy.tabs.login, copy.tabs.signUp]}
                onChange={(index) => {
                    setView(index === 0 ? 'login' : 'join')
                }}
            />
            {isLogin ? (
                <LoginForm copy={copy.login} mode={mode} next={resolvedNext} onSuccess={onAuthSuccess} />
            ) : (
                <JoinForm
                    copy={copy.join}
                    mode={mode}
                    next={resolvedNext}
                    onSuccess={onAuthSuccess}
                    onSwitchToLogin={() => setView('login')}
                />
            )}
            {footer}
        </div>
    )

    if (isModal) {
        return content
    }

    return (
        <>
            <Background />
            <div className={outerContainerClassName}>{content}</div>
        </>
    )
}

export default LoginPageContents
